import { ArchitecturalMetrics } from "src/types/ArchitecturalMetrics";
import { ZoneType } from "src/types/Zone";
import { fetchMetricCalculations } from "src/hooks/useMetricsCalculationsQuery";
import {
  createCarbonViewForTotal,
  createCostingViewForTotal,
} from "src/service/cost/utility/custom-view";
import { getCurrencySymbol } from "src/utils/currency";
import type { World } from "src/types/World";
import type { Api } from "src/types/Api";
import type { StencilMode } from "src/types/MapState";

export interface UnitMixByBedRow {
  /** Bedroom count parsed from the unit category label (e.g. "2B3P" -> 2). */
  beds: number;
  /** Requested share from the design brief (0-1), or null if no brief target. */
  targetShare: number | null;
  /** Generated unit count for this bedroom count. */
  actualCount: number;
  /** Generated share of total units (0-1). */
  actualShare: number;
}

export interface SummaryPdfData {
  projectName: string;
  designName: string;
  exportedAt: Date;

  location: {
    latitude: number;
    longitude: number;
    addressLabel: string | null;
  };

  /** Design-input plan: live map (basemap + overlay + zone outlines, no buildings). */
  sitePlanDataUrl: string | null;
  /** 2D top-down render framing the generated buildings. */
  topDownDataUrl: string | null;
  /** 3D isometric render of the generated buildings. */
  thumbnailDataUrl: string | null;
  /** Live map with the generated design on it, shown beneath the result renders. */
  contextMapDataUrl: string | null;

  zones: Array<{
    id: string;
    label: string;
    zoneType: ZoneType;
    grzActual: number;
    gfzActual: number;
    floorsMax: number;
  }>;

  /** Total area in m² per non-buildable zone type. */
  zoneAreas: {
    parking: number;
    access: number;
    green: number;
    exclusion: number;
  };

  metrics: {
    buildingsCount: number;
    totalUnits: number;
    gfa: number;
    footprint: number;
    floorsMax: number;
    plotAreaHectares: number;
    maxBuildingHeightM: number;
    /** Net-to-gross residential efficiency (0-1). */
    residentialEfficiency: number;
  };

  /** Requested vs generated unit mix, grouped by bedroom count. */
  unitMixByBed: UnitMixByBedRow[];

  design: {
    /** True if any buildable zone's brief includes a podium. */
    podium: boolean;
    /** Requested max building height in m (max across buildable zones), or null. */
    maxBuildingHeightTargetM: number | null;
    buildingSeparation: number;
  };

  stencil: { present: boolean; sourceLabel: string | null };

  carbon: { totalKg: number; perSqmKg: number } | null;

  /** Construction cost breakdown (existing costing engine) for the financial report. */
  cost: {
    currency: string;
    rows: Array<{
      label: string;
      cost: number;
      perUnit: number;
      perSqm: number;
      emphasis?: "subtotal" | "total";
    }>;
  } | null;

  /** Map bearing (degrees) at the on-map capture — rotates the on-map compass. */
  mapBearingDeg: number;
}

export interface BuildSummaryDataInput {
  world: World;
  projectId: string;
  projectName: string;
  api: Api;
  mapboxToken: string;
  stencilMode: StencilMode;
  sitePlanDataUrl: string | null;
  topDownDataUrl: string | null;
  thumbnailDataUrl: string | null;
  contextMapDataUrl: string | null;
  mapBearingDeg?: number;
  geocodeFetch?: typeof fetch;
}

const HECTARE_TO_SQM = 10_000;

export async function buildSummaryData(input: BuildSummaryDataInput): Promise<SummaryPdfData> {
  const {
    world,
    projectId,
    projectName,
    api,
    mapboxToken,
    stencilMode,
    sitePlanDataUrl,
    topDownDataUrl,
    thumbnailDataUrl,
    contextMapDataUrl,
    mapBearingDeg = 0,
    geocodeFetch = fetch,
  } = input;

  const worldMetrics = new ArchitecturalMetrics(world.buildings);

  const zones = world.zones
    .filter((zone) => zone.zoneType === ZoneType.buildable)
    .map((zone) => {
      const m = new ArchitecturalMetrics(zone.buildings);
      return {
        id: zone.id,
        label: zone.label,
        zoneType: zone.zoneType,
        grzActual: m.LandCoverageTotal,
        gfzActual: m.GfzRatio,
        floorsMax: m.FloorMax,
      };
    });

  const zoneAreas = { parking: 0, access: 0, green: 0, exclusion: 0 };
  for (const zone of world.zones) {
    const areaSqm = zone.siteAreaHectares * HECTARE_TO_SQM;
    if (zone.zoneType === ZoneType.parking) zoneAreas.parking += areaSqm;
    else if (zone.zoneType === ZoneType.access) zoneAreas.access += areaSqm;
    else if (zone.zoneType === ZoneType.green_zones) zoneAreas.green += areaSqm;
    else if (zone.zoneType === ZoneType.exclusion) zoneAreas.exclusion += areaSqm;
  }

  const unitMixByBed = buildUnitMixByBed(world, worldMetrics);

  const buildableBriefs = world.zones
    .filter((zone) => zone.zoneType === ZoneType.buildable)
    .map((zone) => zone.brief);
  const podium = buildableBriefs.some((brief) => brief.podium != null);
  const heightTargets = buildableBriefs
    .map((brief) => brief.totalBuildingHeight)
    .filter((h) => h > 0);
  const maxBuildingHeightTargetM = heightTargets.length > 0 ? Math.max(...heightTargets) : null;

  const allBuildingIds = world.buildings.map((b) => b.id);
  let carbon: SummaryPdfData["carbon"] = null;
  let cost: SummaryPdfData["cost"] = null;
  if (allBuildingIds.length > 0) {
    try {
      const calc = await fetchMetricCalculations(api, projectId, world, allBuildingIds);
      if (calc) {
        const gia = worldMetrics.GIATotal;
        const totalKg = createCarbonViewForTotal(calc).summary.total;
        carbon = { totalKg, perSqmKg: gia > 0 ? totalKg / gia : 0 };
        cost = buildCostBreakdown(calc, worldMetrics.ApartmentQuantityTotal, gia);
      }
    } catch {
      carbon = null;
      cost = null;
    }
  }

  const addressLabel = await reverseGeocode({
    longitude: world.longitude,
    latitude: world.latitude,
    token: mapboxToken,
    fetchImpl: geocodeFetch,
  });

  return {
    projectName,
    // Report title defaults to the project name; the export wizard overrides
    // this with a custom name when the user provides one.
    designName: projectName || "Untitled report",
    exportedAt: new Date(),

    location: {
      latitude: world.latitude,
      longitude: world.longitude,
      addressLabel,
    },

    sitePlanDataUrl,
    topDownDataUrl,
    thumbnailDataUrl,
    contextMapDataUrl,

    zones,
    zoneAreas,

    metrics: {
      buildingsCount: worldMetrics.buildingsCount,
      totalUnits: worldMetrics.ApartmentQuantityTotal,
      gfa: worldMetrics.GEATotal,
      footprint: worldMetrics.FootPrintTotal,
      floorsMax: worldMetrics.FloorMax,
      plotAreaHectares: world.siteAreaHectares,
      maxBuildingHeightM: worldMetrics.BuildingHeightMax,
      residentialEfficiency: worldMetrics.ResidentialEfficiencyTotal,
    },

    unitMixByBed,

    design: {
      podium,
      maxBuildingHeightTargetM,
      buildingSeparation: world.brief.buildingSeparation,
    },

    cost,

    stencil: deriveStencil(stencilMode, world.stencilCatalog),

    carbon,

    mapBearingDeg,
  };
}

/**
 * Construction cost breakdown from the existing costing engine — the same
 * numbers the inspector's cost tab shows. perUnit / perSqm are cost ÷ units /
 * cost ÷ GIA. (Land value, rental/sales income and ROI/IRR are not sourced yet,
 * so they're left out of the report.)
 */
function buildCostBreakdown(
  calc: Parameters<typeof createCostingViewForTotal>[0],
  units: number,
  gia: number
): SummaryPdfData["cost"] {
  const s = createCostingViewForTotal(calc).summary;
  const row = (label: string, c: number, emphasis?: "subtotal" | "total") => ({
    label,
    cost: c,
    perUnit: units > 0 ? c / units : 0,
    perSqm: gia > 0 ? c / gia : 0,
    emphasis,
  });
  const construction = s.constructionModules + s.constructionFitOut + s.constructionBaseBuild;
  return {
    currency: getCurrencySymbol(),
    rows: [
      row("Modular", s.constructionModules),
      row("Fit-out", s.constructionFitOut),
      row("Base build", s.constructionBaseBuild),
      row("Construction", construction, "subtotal"),
      row("Preliminaries", s.preliminaries),
      row("Contingency", s.contingencies),
      row("Overheads & profit", s.overheadsAndProfits),
      row("Total cost", s.total, "total"),
    ],
  };
}

/**
 * Groups both the requested (brief) and generated unit mixes by bedroom count.
 * Bedroom count is the leading digit of a unit category label (e.g. "2B3P" -> 2).
 */
function buildUnitMixByBed(world: World, worldMetrics: ArchitecturalMetrics): UnitMixByBedRow[] {
  // Requested mix: sum enabled category ratios across buildable zones, then
  // normalise so a multi-zone brief still reads as a single 0-1 distribution.
  const targetSums = new Map<number, number>();
  let targetTotal = 0;
  for (const zone of world.zones) {
    if (zone.zoneType !== ZoneType.buildable) continue;
    for (const category of zone.brief.unitMix.categories) {
      if (!category.enabled) continue;
      const beds = parseBeds(category.label);
      if (beds == null) continue;
      targetSums.set(beds, (targetSums.get(beds) ?? 0) + category.value);
      targetTotal += category.value;
    }
  }

  // Generated mix: sum unit counts by bedroom count.
  const actualCounts = new Map<number, number>();
  let actualTotal = 0;
  for (const item of worldMetrics.UnitCategoryMixPerCategory ?? []) {
    if (item.count <= 0) continue;
    const beds = parseBeds(item.name);
    if (beds == null) continue;
    actualCounts.set(beds, (actualCounts.get(beds) ?? 0) + item.count);
    actualTotal += item.count;
  }

  const beds = [...new Set([...targetSums.keys(), ...actualCounts.keys()])].sort((a, b) => a - b);
  return beds.map((bed) => {
    const targetRaw = targetSums.get(bed);
    const actualCount = actualCounts.get(bed) ?? 0;
    return {
      beds: bed,
      targetShare: targetTotal > 0 && targetRaw != null ? targetRaw / targetTotal : null,
      actualCount,
      actualShare: actualTotal > 0 ? actualCount / actualTotal : 0,
    };
  });
}

function parseBeds(label: string): number | null {
  const beds = Number.parseInt(label.trim()[0] ?? "", 10);
  return Number.isFinite(beds) ? beds : null;
}

interface ReverseGeocodeArgs {
  longitude: number;
  latitude: number;
  token: string;
  fetchImpl: typeof fetch;
}

async function reverseGeocode(args: ReverseGeocodeArgs): Promise<string | null> {
  if (!args.token) return null;
  if (!Number.isFinite(args.latitude) || !Number.isFinite(args.longitude)) return null;
  try {
    const res = await args.fetchImpl(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${args.longitude},${args.latitude}.json?access_token=${args.token}&types=address,place,locality,neighborhood&limit=1`
    );
    if (!res.ok) return null;
    const data: { features?: Array<{ place_name?: string }> } = await res.json();
    return data.features?.[0]?.place_name ?? null;
  } catch {
    return null;
  }
}

function deriveStencil(
  mode: StencilMode,
  catalog: World["stencilCatalog"]
): SummaryPdfData["stencil"] {
  if (mode.kind === "none") return { present: false, sourceLabel: null };
  if (mode.kind === "wms") {
    const layer = mode.capabilities.layers.find((l) => l.name === mode.selectedLayerName);
    const label =
      layer?.title ?? mode.capabilities.serviceTitle ?? catalog[0]?.title ?? "WMS overlay";
    return { present: true, sourceLabel: label };
  }
  return { present: true, sourceLabel: "Image overlay" };
}
